import type { ICodeType } from '@/services/iCodeService';

// Routes that can appear in allowedRoutes / landingRoute. Kept as a union so
// the compiler catches typos when new iCode types are added.
export type AppRoute =
  | '/dashboard'
  | '/dashboard-ai'
  | '/exams'
  | '/appointments'
  | '/health-summary'
  | '/messages'
  | '/share'
  | '/account'
  | '/gfe'
  | '/help'
  | '/schedule';

export interface ICodeCapabilities {
  allowedRoutes: ReadonlySet<AppRoute>;
  landingRoute: AppRoute;
}

// One entry per iCode type that produces an active session. Types 'C' and
// 'MEDICAL_RECORD_ACCESS' never reach here — they're handled during lookup
// in ICodeVerify (redirect to /register or block, respectively). 'GFE' is
// intentionally excluded — it resolves to one of the two variants below
// instead of a single fixed entry (see GFE_REPORTED_CAPABILITIES).
export const ICODE_CAPABILITIES: Partial<Record<Exclude<ICodeType, 'GFE'>, ICodeCapabilities>> = {
  S: {
    allowedRoutes: new Set<AppRoute>(['/exams', '/health-summary', '/help']),
    landingRoute: '/exams',
  },
  O: {
    allowedRoutes: new Set<AppRoute>(['/schedule', '/help']),
    landingRoute: '/schedule',
  },
  R: {
    allowedRoutes: new Set<AppRoute>(['/schedule', '/help']),
    landingRoute: '/schedule',
  },
  LAYLETTER: {
    allowedRoutes: new Set<AppRoute>(['/exams', '/help']),
    landingRoute: '/exams',
  },
};

// GFE iCodes fork into two mutually-exclusive capability sets based on
// ICodeSession.isExamReported (set from ICodeContext.isExamReported at verify
// time). A signed/already-reported study lands on /exams and must never be
// able to reach /appointments (or /messages); an order-only/not-yet-reported
// study lands on /appointments and must never reach /exams (or /messages).
// Resolved in useSessionCaps.ts rather than keyed into ICODE_CAPABILITIES
// since both variants share the single iCodeType 'GFE'.
export const GFE_REPORTED_CAPABILITIES: ICodeCapabilities = {
  allowedRoutes: new Set<AppRoute>(['/exams', '/help']),
  landingRoute: '/exams',
};

export const GFE_UNREPORTED_CAPABILITIES: ICodeCapabilities = {
  allowedRoutes: new Set<AppRoute>(['/appointments', '/help']),
  landingRoute: '/appointments',
};

// Safe fallback for an unknown iCode type: lock everything except /help.
export const DEFAULT_ICODE_CAPS: ICodeCapabilities = {
  allowedRoutes: new Set<AppRoute>(['/help']),
  landingRoute: '/help',
};
